import { GetStorageDirectory } from "@pdfking/capability"
import type { FileName, URLString } from "@pdfking/structs"
import type { StringGUID } from "@pdfking/tfunc"
import { SafePromise } from "@pdfking/tfunc"

import type { RemoteFileSystem } from "#public"

import { ImplementHandleFileSystem } from "../../internal/ImplementHandleFileSystem"
import { StorageUrl } from "../../public/utils/StorageUrl"

const OpfsRoot = (): Promise<FileSystemDirectoryHandle> => {
  const root = GetStorageDirectory()
  if (!root) {
    throw new Error("navigator.storage.getDirectory is unavailable: this needs OPFS")
  }
  return root()
}

/** One folder of the origin's own private directory, as a filesystem.
 *
 * The same directory handles the picker hands out, minus the picking: the
 * origin already owns this one, so there is no prompt, no grant to lose across
 * a reload, and nothing to remember between visits. Which makes it the one
 * real filesystem a browser has that a caller can simply open — nothing can
 * refuse it, so there is no result to unwrap. A caller on a runtime without
 * OPFS asks `GetStorageDirectory` first.
 *
 * The guid is the folder — one store per name, side by side under the same
 * private root — and `clear` drops that folder rather than walking it: the
 * next call makes it again, empty.
 *
 * Entries have urls, `opfs://{folder}/{path}`: the origin's own directory is
 * always there to be opened, so the folder is the whole of what it takes to
 * find this store again. */
export const OpfsFileSystem = (guid: StringGUID): RemoteFileSystem => ({
  ...ImplementHandleFileSystem(() =>
    OpfsRoot().then(root => root.getDirectoryHandle(guid, { create: true }))
  ),

  url: (name: FileName): URLString => StorageUrl("opfs:", guid, name),

  // The one root that can be dropped rather than emptied: this folder is ours
  // and the thunk makes it again on the next call. A folder nobody made is
  // already clear, so NotFoundError is an answer, not a failure.
  async clear(): Promise<void> {
    await SafePromise((await OpfsRoot()).removeEntry(guid, { recursive: true }))
  },
})
